/**
 * Adding documents to the library, as one dialog: drop or pick PDFs (several
 * at once), say what each one is — a new policy, or the next version of one
 * already here — tag the new ones with jurisdictions, upload.
 *
 * The "new vs version" default is guessed from the filename against the
 * library, because a re-exported "Sanctions Procedure v4.pdf" is nearly
 * always an update and treating it as a second policy is the mistake this
 * dialog exists to prevent. Every guess is a select the person can change.
 *
 * Jurisdictions are optional: left empty, ingestion detects them from the
 * text and marks them as detected rather than chosen.
 *
 * @module components/regulation/PolicyUploadDialog
 */
import {
  CheckCircle2Icon,
  FilePlusIcon,
  FileTextIcon,
  ExternalLinkIcon,
  FileUpIcon,
  HistoryIcon,
  Loader2Icon,
  UploadIcon,
  XCircleIcon,
  XIcon,
} from "lucide-react";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "~/lib/utils";
import {
  policyDisplayName,
  policyVersion,
  usePolicies,
  type Policy,
} from "../../state/policies";
import { Button } from "../ui/button";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxValue,
} from "../ui/combobox";
import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { formatFileSize, suggestVersionTarget } from "./policyUploadMatch";

const MAX_BYTES = 25 * 1024 * 1024;
/** Sentinel for the "new policy" option in each row's select. */
const NEW = "__new__";

interface Row {
  readonly key: string;
  readonly file: File;
  /** NEW, or the id of the policy this file is the next version of. */
  readonly target: string;
  readonly status: "ready" | "uploading" | "done" | "failed";
  readonly error: string | null;
  /** Rejected before upload: not a PDF, or too large. */
  readonly invalid: string | null;
  /** The policy this row ended up in, once uploaded — for the Open link. */
  readonly resultId: string | null;
}

const validate = (file: File): string | null =>
  !file.name.toLowerCase().endsWith(".pdf")
    ? "Only PDFs"
    : file.size > MAX_BYTES
      ? "Over 25 MB"
      : file.size === 0
        ? "Empty file"
        : null;

export function PolicyUploadDialog({
  organizationId,
  open,
  onOpenChange,
  initialFiles,
  documentKind = "policy",
}: {
  readonly organizationId: string | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Files dropped on the page before the dialog opened, if any. */
  readonly initialFiles?: ReadonlyArray<File>;
  readonly documentKind?: "policy" | "procedure";
}) {
  const { snapshot, uploadDocument, replaceFile } = usePolicies(organizationId);
  // The tenant segment of the page this dialog sits on, for links to what
  // was just uploaded; absent (no org route) the links simply do not render.
  const orgSegment = useParams({
    strict: false,
    select: (params) => (params as { org?: string }).org ?? null,
  });
  const policies = useMemo(() => snapshot?.policies ?? [], [snapshot]);
  const orgJurisdictions = snapshot?.jurisdictions ?? [];
  const jurisdictionName = useCallback(
    (slug: string) => orgJurisdictions.find((entry) => entry.slug === slug)?.name ?? slug,
    [orgJurisdictions],
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ReadonlyArray<Row>>([]);
  const [jurisdictions, setJurisdictions] = useState<ReadonlyArray<string>>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);

  const addFiles = useCallback(
    (files: ReadonlyArray<File>) => {
      setRows((current) => {
        const seen = new Set(current.map((row) => `${row.file.name}:${row.file.size}`));
        const next = files
          .filter((file) => !seen.has(`${file.name}:${file.size}`))
          .map<Row>((file) => ({
            key: `${file.name}:${file.size}:${file.lastModified}`,
            file,
            target: suggestVersionTarget(file.name, policies)?.id ?? NEW,
            status: "ready",
            error: null,
            invalid: validate(file),
            resultId: null,
          }));
        return [...current, ...next];
      });
    },
    [policies],
  );

  // Every opening starts clean, seeded with whatever was dropped on the page.
  useEffect(() => {
    if (!open) return;
    setRows([]);
    setJurisdictions([]);
    setNote("");
    setBusy(false);
    if (initialFiles && initialFiles.length > 0) addFiles(initialFiles);
    // addFiles changes with the library; seeding must happen once per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFiles]);

  const pending = rows.filter((row) => row.invalid === null && row.status !== "done");
  // Two files filed as versions of the same policy would race each other
  // into v3 and v4 with no say in the order; make it a decision instead.
  const conflictingTargets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of pending) {
      if (row.target !== NEW) counts.set(row.target, (counts.get(row.target) ?? 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([id]) => id));
  }, [pending]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const newCount = pending.filter((row) => row.target === NEW).length;
  const versionCount = pending.length - newCount;
  const allDone = rows.length > 0 && rows.every((row) => row.status === "done" || row.invalid !== null);

  const submit = async () => {
    setBusy(true);
    const trimmed = note.trim();
    const queue = rows.filter((row) => row.invalid === null && row.status !== "done");
    let finished = 0;
    setProgress({ done: 0, total: queue.length });
    for (const row of queue) {
      setRows((current) =>
        current.map((entry) => (entry.key === row.key ? { ...entry, status: "uploading", error: null } : entry)),
      );
      let error: string | null;
      let resultId: string | null = null;
      if (row.target === NEW) {
        const outcome = await uploadDocument({ file: row.file, jurisdictionSlugs: jurisdictions });
        error = outcome.error;
        resultId = outcome.value?.id ?? null;
      } else {
        error = await replaceFile(row.target, row.file, trimmed.length === 0 ? null : trimmed);
        resultId = error === null ? row.target : null;
      }
      finished += 1;
      setProgress({ done: finished, total: queue.length });
      setRows((current) =>
        current.map((entry) =>
          entry.key === row.key
            ? { ...entry, status: error === null ? "done" : "failed", error, resultId }
            : entry,
        ),
      );
    }
    setBusy(false);
    setProgress(null);
  };

  const noun = documentKind === "procedure" ? "procedure" : "policy";
  const submitLabel =
    pending.length === 0
      ? "Upload"
      : [
          newCount > 0 ? `${newCount} new` : null,
          versionCount > 0 ? `${versionCount} ${versionCount === 1 ? "version" : "versions"}` : null,
        ]
          .filter(Boolean)
          .join(" · ");

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing mid-upload would leave half the batch unreported.
        if (!next && busy) return;
        onOpenChange(next);
      }}
    >
      <DialogPopup className="flex max-h-[85vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>Add {documentKind === "procedure" ? "procedures" : "policies"}</DialogTitle>
          <DialogDescription>
            Drop PDFs below. A file that matches something already in the library is
            filed as its next version; the rest become new {noun === "policy" ? "policies" : "procedures"}.
          </DialogDescription>
        </DialogHeader>

        <DialogPanel className="min-h-0 flex-1 space-y-5 overflow-y-auto">
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={(event) => {
              addFiles(Array.from(event.target.files ?? []));
              event.target.value = "";
            }}
          />
          {/* The drop zone stays visible with rows below it: adding one more
              file should not mean starting over. */}
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              addFiles(Array.from(event.dataTransfer?.files ?? []));
            }}
            className={cn(
              "flex w-full cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed px-4 text-center transition-colors",
              rows.length === 0 ? "py-10" : "py-5",
              dragging
                ? "border-primary/60 bg-primary/5"
                : "border-border hover:border-foreground/30 hover:bg-muted/40",
            )}
          >
            <FileUpIcon className="size-5 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              {rows.length === 0 ? "Drop PDFs here, or click to choose" : "Add more"}
            </span>
            <span className="text-xs text-muted-foreground">PDF · up to 25 MB each</span>
          </button>

          {rows.length > 0 ? (
            <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60">
              {rows.map((row) => {
                const target =
                  row.target === NEW
                    ? null
                    : (policies.find((policy) => policy.id === row.target) ?? null);
                return (
                  <li key={row.key} className="flex items-center gap-3 px-3 py-2.5">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted">
                      {row.status === "done" ? (
                        <CheckCircle2Icon className="size-4 text-success" />
                      ) : row.status === "failed" || row.invalid !== null ? (
                        <XCircleIcon className="size-4 text-destructive" />
                      ) : row.status === "uploading" ? (
                        <Loader2Icon className="size-4 animate-spin text-muted-foreground" />
                      ) : (
                        <FileTextIcon className="size-4 text-muted-foreground" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{row.file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(row.file.size)}
                        {row.invalid !== null ? (
                          <span className="text-destructive"> · {row.invalid}</span>
                        ) : row.error !== null ? (
                          <span className="text-destructive"> · {row.error}</span>
                        ) : row.status === "done" ? (
                          <span className="text-success">
                            {" "}
                            · {target ? `Now v${policyVersion(target) + 1} of ${policyDisplayName(target)}` : "Added to the library"}
                          </span>
                        ) : target ? (
                          conflictingTargets.has(target.id) ? (
                            <span className="text-destructive">
                              {" "}
                              · another file is also a version of {policyDisplayName(target)}
                            </span>
                          ) : (
                            <span>
                              {" "}
                              · becomes v{policyVersion(target) + 1} of {policyDisplayName(target)}
                            </span>
                          )
                        ) : null}
                      </p>
                    </div>
                    {row.status === "done" && row.resultId !== null && orgSegment !== null ? (
                      <Link
                        to="/$org/compliance/policies/$policyId"
                        params={{ org: orgSegment, policyId: row.resultId }}
                        className="flex shrink-0 cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        onClick={() => onOpenChange(false)}
                      >
                        <ExternalLinkIcon className="size-3.5" />
                        Open
                      </Link>
                    ) : null}
                    {row.invalid === null && row.status !== "done" ? (
                      <Select
                        value={row.target}
                        onValueChange={(value) =>
                          setRows((current) =>
                            current.map((entry) =>
                              entry.key === row.key ? { ...entry, target: value ?? NEW } : entry,
                            ),
                          )
                        }
                      >
                        <SelectTrigger
                          size="sm"
                          className="w-60 cursor-pointer"
                          aria-label={`What ${row.file.name} is`}
                          disabled={busy}
                        >
                          <span className="flex min-w-0 items-center gap-1.5">
                            {target ? (
                              <HistoryIcon className="size-3.5 shrink-0 text-muted-foreground" />
                            ) : (
                              <FilePlusIcon className="size-3.5 shrink-0 text-muted-foreground" />
                            )}
                            <span className="truncate">
                              {target
                                ? `Version of ${policyDisplayName(target)}`
                                : `New ${noun}`}
                            </span>
                            {target ? (
                              <span className="shrink-0 text-xs text-muted-foreground">
                                v{policyVersion(target)} → v{policyVersion(target) + 1}
                              </span>
                            ) : null}
                          </span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NEW}>New {noun}</SelectItem>
                          {policies.map((policy) => (
                            <SelectItem key={policy.id} value={policy.id}>
                              Version of {policyDisplayName(policy)}
                              <span className="ml-1.5 text-xs text-muted-foreground">
                                v{policyVersion(policy)}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : null}
                    {row.status !== "uploading" && row.status !== "done" ? (
                      <button
                        type="button"
                        aria-label={`Remove ${row.file.name}`}
                        className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        onClick={() =>
                          setRows((current) => current.filter((entry) => entry.key !== row.key))
                        }
                      >
                        <XIcon className="size-3.5" />
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}

          {newCount > 0 ? (
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                Jurisdictions for the new {newCount === 1 ? noun : `${noun === "policy" ? "policies" : "procedures"}`}
              </span>
              <Combobox
                multiple
                items={orgJurisdictions.map((jurisdiction) => jurisdiction.slug)}
                value={[...jurisdictions]}
                onValueChange={(next) => setJurisdictions(next)}
                itemToStringLabel={(slug) => jurisdictionName(slug)}
                disabled={busy}
              >
                <ComboboxChips>
                  <ComboboxValue>
                    {(value: ReadonlyArray<string>) =>
                      value.map((slug) => (
                        <ComboboxChip key={slug} aria-label={jurisdictionName(slug)}>
                          {jurisdictionName(slug)}
                        </ComboboxChip>
                      ))
                    }
                  </ComboboxValue>
                  <ComboboxChipsInput
                    placeholder={jurisdictions.length === 0 ? "Leave empty to detect from the text" : undefined}
                  />
                </ComboboxChips>
                <ComboboxPopup>
                  <ComboboxEmpty>No matching jurisdiction.</ComboboxEmpty>
                  <ComboboxList>
                    {(slug: string) => (
                      <ComboboxItem key={slug} value={slug}>
                        {jurisdictionName(slug)}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxPopup>
              </Combobox>
            </div>
          ) : null}

          {versionCount > 0 ? (
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                What changed in the new {versionCount === 1 ? "version" : "versions"} (optional)
              </span>
              <Input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="e.g. Annual review — updated PEP screening thresholds"
                disabled={busy}
              />
            </label>
          ) : null}
        </DialogPanel>

        <DialogFooter>
          <DialogClose
            render={
              <Button type="button" size="sm" variant="ghost" className="cursor-pointer">
                {allDone ? "Done" : "Cancel"}
              </Button>
            }
          />
          {!allDone ? (
            <Button
              type="button"
              size="sm"
              className="cursor-pointer gap-1.5"
              disabled={busy || pending.length === 0 || conflictingTargets.size > 0}
              title={conflictingTargets.size > 0 ? "Two files are versions of the same policy — pick one" : undefined}
              onClick={() => void submit()}
            >
              {busy ? <Loader2Icon className="size-3.5 animate-spin" /> : <UploadIcon className="size-3.5" />}
              {busy && progress
                ? `Uploading ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…`
                : `Upload ${submitLabel}`}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
